// ============================================================
// LESSON PATTERNS: logic only (guitar)
// ============================================================
//
// Turns the private lesson pattern data into board positions in any major key.
// This file holds NO pattern data. The data lives in api/_lesson-data.js and reaches the browser
// only through the Sign in (api/lesson-patterns.js -> useLessonAccess().patterns); its format is
// documented there. Signed out, `patterns` is null and every function here returns null or [],
// so the guitar page is the public page.
//
// Where the patterns show (Key card follow-up trays, Structure = Scale, Major only): the 7 major
// patterns, numbered as on the sheet, and the 5 blues patterns (1 b3 4 5 b7 of the key's blues
// scale: 1 b3 4 b5 5 b7). Minor keys get none for now (the owner dropped them; the relative-major
// version is in git history, commit 61c5b59). Tray names come from the data (`trayLabels`), so
// the teacher's name never ships in the public bundle.

import { DEGREE_LABELS, noteIndex, getNoteName, usesFlats } from './theory';

export const MAJOR_FORMULA = [0, 2, 4, 5, 7, 9, 11];
/** Blues scale: 1 b3 4 b5 5 b7. Every blues pattern on the sheet includes the b5. */
export const BLUES_FORMULA = [0, 3, 5, 6, 7, 10];

const GUITAR_OPEN = [4, 9, 2, 7, 11, 4]; // low E .. high e, semitone indices

function isValidPattern(p) {
  return Boolean(p) && typeof p.id === 'string' && typeof p.label === 'string'
    && (p.group === 'major' || p.group === 'blues')
    && Number.isInteger(p.start)
    && Array.isArray(p.frets) && p.frets.length === GUITAR_OPEN.length
    && p.frets.every((s) => Array.isArray(s) && s.every(Number.isInteger));
}

function patternsOf(data, group) {
  const list = data && Array.isArray(data.patterns) ? data.patterns : [];
  return list.filter((p) => isValidPattern(p) && p.group === group);
}

/**
 * @typedef {object} LessonPattern  a pattern placed for one view
 * @property {string}     id         the data's id (the state value)
 * @property {string}     group      'major' | 'blues'
 * @property {string}     label      the number on the sheet, what the tray shows
 * @property {number}     start      semitones from the key's root to the start note
 * @property {number[][]} frets      offsets from the anchor, per string (from the data)
 * @property {number[]}   formula    scale tones relative to the displayed key
 */

/** Tray name when the data doesn't give one. */
const FALLBACK_TRAY_LABEL = { major: 'Patterns', blues: 'Blues patterns' };

/**
 * The lesson trays for a guitar view, or null when there are none (signed out, Minor, or a
 * view other than Scale).
 * @param {object|null} data      useLessonAccess().patterns
 * @param {string}      structure 'Full Scale' | 'Pentatonic' | 'Modes' | 'Arpeggios'
 * @param {string}      tonality  'Major' | 'Minor'
 * @returns {{ group: 'major' | 'blues', label: string, patterns: LessonPattern[] }[] | null}
 */
export function lessonTraysFor(data, structure, tonality) {
  if (!data || structure !== 'Full Scale' || String(tonality).toLowerCase() !== 'major') return null;
  const labelOf = (group) => {
    const label = data.trayLabels && data.trayLabels[group];
    return typeof label === 'string' && label ? label : FALLBACK_TRAY_LABEL[group];
  };
  const trays = [];

  const major = patternsOf(data, 'major').map((p) => ({ ...p, formula: MAJOR_FORMULA }));
  if (major.length) trays.push({ group: 'major', label: labelOf('major'), patterns: major });

  const blues = patternsOf(data, 'blues').map((p) => ({ ...p, formula: BLUES_FORMULA }));
  if (blues.length) trays.push({ group: 'blues', label: labelOf('blues'), patterns: blues });

  return trays.length ? trays : null;
}

/** The pattern `id` among the trays, or null. */
export function findLessonPattern(trays, id) {
  if (!trays || !id) return null;
  for (const tray of trays) {
    const hit = tray.patterns.find((p) => p.id === id);
    if (hit) return hit;
  }
  return null;
}

/** Lowest fret (0-11) of the pattern's start note on the low E string in `key`. */
export function lessonAnchorFret(key, pattern) {
  return (((noteIndex(key) + pattern.start - GUITAR_OPEN[0]) % 12) + 12) % 12;
}

/**
 * The fret the pattern is drawn from in `key`: its anchor, or an octave up when a note reaching
 * behind the anchor (offset -1) would fall below the nut. The highest note this gives is fret 16,
 * well inside the 24-fret neck.
 */
export function lessonPatternBase(key, pattern) {
  const anchor = lessonAnchorFret(key, pattern);
  return anchor + Math.min(...pattern.frets.flat()) < 0 ? anchor + 12 : anchor;
}

/**
 * Every note of the pattern in `key`, in ONE place on the neck: the lowest position where the
 * whole pattern fits. Unlike the CAGED windows there is no copy an octave up (the owner wants
 * each pattern shown once).
 * @returns {{ string: number, fret: number }[]}
 */
export function lessonPatternNotes(key, pattern) {
  const base = lessonPatternBase(key, pattern);
  return pattern.frets.flatMap((offsets, string) => offsets.map((offset) => ({ string, fret: base + offset })));
}

/** lessonPatternNotes as a Set of "string-fret" keys, for the board's visibility check. */
export function lessonPatternPositions(key, pattern) {
  return new Set(lessonPatternNotes(key, pattern).map((n) => `${n.string}-${n.fret}`));
}

/** Page title while a blues pattern is shown, e.g. "G Blues Scale". */
export function getBluesTitle(key) {
  return `${key} Blues Scale`;
}

/** Heading for the notes row while a blues pattern is shown. */
export const BLUES_NOTES_HEADING = 'Notes in the blues scale';

/**
 * Name of a blues-scale tone in `key`. Every tone but the b5 is spelled as the key's minor
 * pentatonic is on the board (theory.js usesFlats, minor rule: G gives Bb and F); the b5 always
 * takes its flat or natural name (G: Db, A: Eb, E: Bb, B: F). The board and the notes row both
 * call this, so they always agree, whatever Major/Minor says.
 * @param {string} key
 * @param {number} interval semitones from the root
 */
export function spellBluesTone(key, interval) {
  const pc = noteIndex(key) + interval;
  return getNoteName(pc, interval === 6 ? true : usesFlats(key, BLUES_FORMULA, 'minor'));
}

/**
 * The blues scale's notes for the notes row (G gives G Bb C Db D F, as on the sheet).
 * @returns {{ name: string, degree: string, isRoot: boolean }[]}
 */
export function getBluesNotes(key) {
  return BLUES_FORMULA.map((interval) => ({
    name: spellBluesTone(key, interval),
    degree: DEGREE_LABELS[interval],
    isRoot: interval === 0,
  }));
}
